import { useContext, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import Button from "../components/button";
import Input from "../components/input";
import { MachinePreview } from "../components/machinePreview";
import Modal from "../components/modal";
import PageHero from "../components/pageHero.jsx";
import { contactInformation } from "../constants";
import { MachineContext } from "../context/MachineContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { createSellProposal } from "../httpService";
import { absoluteUrl, isNewMachine } from "../seoUtils";

const Quotation = () => {
  const { id } = useParams();
  const { machines } = useContext(MachineContext);
  const { locale } = useLocalePath();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");
  const [organizationNumber, setOrganizationNumber] = useState("");
  const [comment, setComment] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [organizationNumberError, setOrganizationNumberError] = useState("");

  const [requestIsLoading, setRequestIsLoading] = useState(false);

  const validateInputs = () => {
    let isValid = true;
    if (!firstName.trim()) {
      setFirstNameError("Förnamn krävs.");
      isValid = false;
    } else {
      setFirstNameError("");
    }

    if (!lastName.trim()) {
      setLastNameError("Efternamn krävs.");
      isValid = false;
    } else {
      setLastNameError("");
    }

    if (!email.trim()) {
      setEmailError("Email krävs.");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Ogiltigt email-format.");
      isValid = false;
    } else {
      setEmailError("");
    }

    const trimmedOrganizationNumber = organizationNumber.trim().replace("-", "");

    if (!trimmedOrganizationNumber) {
      setOrganizationNumberError("Organisationsnummer krävs.");
      isValid = false;
    } else if (
      trimmedOrganizationNumber.length < 7 ||
      trimmedOrganizationNumber.length > 10 ||
      !/^\d+$/.test(trimmedOrganizationNumber)
    ) {
      setOrganizationNumberError("Ogiltigt organisationsnummer.");
      isValid = false;
    } else {
      setOrganizationNumberError("");
    }

    return isValid;
  };

  const machine = useMemo(() => machines.find((m) => m.id === Number(id)), [machines, id]);

  if (!machine) return null;

  const isNew = isNewMachine(machine);
  const quotationUrl = absoluteUrl(locale, `/machines/${machine.id}/quotation`);

  return (
    <>
      <Helmet>
        <title>AS Maskiner - Begär offert</title>
        <meta
          name="description"
          content={`Få ett skräddarsytt prisförslag på ${machine.title} från AS Maskiner. Upptäck vårt engagemang för kvalitet och service. Perfekt för dina behov inom trä- och plastindustrin.`}
        />
        <meta
          name="keywords"
          content={`AS Maskiner, ${isNew ? "nya" : "begagnade"} maskiner, ${machine.title}, ${isNew ? "ny" : "begagnad"} ${machine.category}, ${machine.manufacturer}, offertförfrågan, träindustri, plastindustri`}
        />
        <meta property="og:url" content={quotationUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: machine.title,
            url: absoluteUrl(locale, `/machines/${machine.id}`),
            image: machine.images[0],
            description: `Få ett skräddarsytt prisförslag på ${machine.title} från AS Maskiner.`,
            brand: {
              "@type": "Brand",
              name: machine.manufacturer,
            },
            sku: String(machine.id),
            category: machine.category,
          })}
        </script>
      </Helmet>

      <PageHero
        title={`Offert – ${machine.title}`}
        titleKey="company.name"
        titleFallback="Offert"
        subtitleKey="company.tagline"
        backgroundKey="companybar.background"
        alignment="bottom"
      />

      <div className="container mx-auto px-4 py-10 max-w-7xl">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Form card */}
          <div className="flex-1 bg-surface shadow-sm border border-border-default p-8 flex flex-col gap-5">
            <h2 className="text-xl font-semibold text-text-heading">Dina uppgifter</h2>

            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  id="firstname"
                  data-testid="firstname-input"
                  label="Förnamn"
                  required={true}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  error={firstNameError}
                />
              </div>
              <div className="flex-1">
                <Input
                  type="text"
                  id="lastname"
                  data-testid="lastname-input"
                  label="Efternamn"
                  required={true}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  error={lastNameError}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="tel"
                  id="telephone"
                  label="Telefonnummer"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Input
                  type="email"
                  id="email"
                  data-testid="email-input"
                  label="Email"
                  required={true}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={emailError}
                />
              </div>
            </div>

            <div className="md:w-1/2 md:pr-2">
              <Input
                type="text"
                id="organizationNumber"
                data-testid="organizationNumber-input"
                label="Organisationsnummer"
                required={true}
                value={organizationNumber}
                onChange={(e) => setOrganizationNumber(e.target.value)}
                error={organizationNumberError}
              />
            </div>

            <div>
              <label htmlFor="comment" className="block text-text-body text-sm font-bold mb-2">
                Kommentar
              </label>
              <textarea
                id="comment"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="border border-border-default rounded px-4 py-3 bg-surface focus:outline-none focus:ring-2 focus:ring-primary w-full resize-y"
              />
            </div>

            <div className="flex justify-end">
              <Button
                size="lg"
                loading={requestIsLoading}
                data-testid="send-quotation-request-button"
                onClick={() => {
                  if (validateInputs()) {
                    setRequestIsLoading(true);
                    createSellProposal({
                      machineId: machine.id,
                      firstName,
                      lastName,
                      phoneNumber: telephone,
                      email,
                      comment,
                      organizationNumber,
                    })
                      .then(() => {
                        setApiResponse({
                          successful: true,
                          message:
                            "Din offertförfrågan är mottagen. Vi kontaktar dig så snart som möjligt.",
                        });
                        setIsModalOpen(true);
                        window.dataLayer?.push({ event: "quotation_created" });
                      })
                      .catch((err) => {
                        setApiResponse({
                          successful: false,
                          message:
                            err.status === 409
                              ? "Du har redan en pågående offertförfrågan för den här maskinen."
                              : `Kunde inte skapa offertförfrågan. Vänligen kontakta oss istället på ${contactInformation.telephoneNumber}.`,
                        });
                        window.dataLayer?.push({ event: "quotation_creation_failed" });
                        setIsModalOpen(true);
                      })
                      .finally(() => setRequestIsLoading(false));
                  }
                }}
              >
                Skicka förfrågan
              </Button>
            </div>
          </div>

          {/* Machine preview */}
          <MachinePreview
            machine={machine}
            sx="w-full lg:max-w-sm lg:self-start"
            showQBtn={false}
          />
        </div>
      </div>

      {isModalOpen && !!apiResponse && (
        <Modal
          header={
            apiResponse.successful ? "Offertförfrågan skapad" : "Offertförfrågan misslyckades"
          }
          onClose={() => {
            if (apiResponse.successful) {
              setFirstName("");
              setLastName("");
              setTelephone("");
              setEmail("");
              setComment("");
              setOrganizationNumber("");
            }
            setIsModalOpen(false);
            setApiResponse(null);
          }}
        >
          <p>{apiResponse.message}</p>
        </Modal>
      )}
    </>
  );
};

export default Quotation;
